<template>
  <FeeScheduleItemLayout
    :section-classes="[$style['fee-schedule-item']]"
    :header-classes="[$style['title-row']]"
    :title-classes="[$style['title-left']]"
    :action-ct-classes="[$style['action-buttons-container']]"
    :show-divider="(isProgramBasedMode && schedule.isMaxFee) || !schedule.isMaxFee"
  >
    <template #title>
      <mds-radio-button
        v-if="isSelectionEnabled"
        :checked="schedule.isActived"
        name="selected-schedule"
        :value="schedule.id"
        @click.native="$emit('schedule-selected', schedule.id)"
      >
      </mds-radio-button>
      <component
        :is="currentTitleCmp"
        :schedule="schedule"
        :show-metadata="!showDetail"
        :is-program-based-mode="isProgramBasedMode"
        :program-options="programOptions"
        :show-info-icons="showInfoIcons"
      ></component>
    </template>
    <template #action>
      <mds-button
        v-if="isEditable"
        :id="`item-edit-${schedule.id}`"
        :disabled="modifyingSchedule"
        type="button"
        variation="icon-only"
        icon="ellipsis-circle"
        @click="showEditDropDownMenu = !showEditDropDownMenu"
      >
      </mds-button>
      <!-- DOMException might occur without below div tag -->
      <div>
        <mds-popover
          v-model="showEditDropDownMenu"
          :triggered-by="`item-edit-${schedule.id}`"
          :position="['bottom-center', 'bottom-left']"
          :class="$style['fee-schedule-item-actions__popover']"
          width="200px"
        >
          <mds-list-group>
            <mds-list-group-item :disabled="!isFeeScheduleEditable" @click.native="handleEdit">
              {{ translate('edit') }}
            </mds-list-group-item>
            <mds-list-group-item @click.native="$emit('delete-schedule', schedule.id)">
              {{ translate('delete') }}
            </mds-list-group-item>
          </mds-list-group>
        </mds-popover>
      </div>
    </template>
    <FeeScheduleDetailsSection
      v-if="!schedule.isMaxFee || (schedule.isMaxFee && isProgramBasedMode)"
      v-model="showDetail"
      :title="translate('detail')"
    >
      <component
        :is="currentDetailsViewCmp"
        :mwc-id="getMwcIdForChild('feeScheduleDetailsView')"
        :schedule="schedule"
        :is-program-based-mode="isProgramBasedMode"
        :program-options="programOptions"
      />
    </FeeScheduleDetailsSection>
  </FeeScheduleItemLayout>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsButton } from '@mds/button';
import MdsRadioButton from '@mds/radio-button';
import { MdsListGroup, MdsListGroupItem } from '@mds/list-group';
import MdsPopover from 'hypo-shared-utils/src/components/ScrollablePopover';
import FeeScheduleItemLayout from './common/layout/FeeScheduleItemLayout';
import FeeScheduleDetailsSection from './common/FeeScheduleDetailsSection';
import { FEE_SCHEDULE_TYPES } from './_config/constants';
import AssetBasedFeeScheduleTitle from './common/title/AssetBasedFeeScheduleTitle';
import HistoricMaxFeeTitle from './common/title/HistoricMaxFeeTitle';
import MaxFeeTitle from './common/title/MaxFeeTitle';
import FeeScheduleDetailsView from './fees/schedule/FeeScheduleDetailsView';
import HistoricMaxFeeDetailsView from './fees/historic-max-fee/HistoricMaxFeeDetailsView';
import MaxFeeDetailsView from './fees/max-fee/MaxFeeDetailsView';

export default {
  name: 'fee-schedule-item-display',
  components: {
    MdsButton,
    MdsRadioButton,
    MdsPopover,
    MdsListGroup,
    MdsListGroupItem,
    FeeScheduleItemLayout,
    FeeScheduleDetailsView,
    FeeScheduleDetailsSection,
    AssetBasedFeeScheduleTitle,
    HistoricMaxFeeTitle,
    MaxFeeTitle,
    HistoricMaxFeeDetailsView,
    MaxFeeDetailsView,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => {},
    },
    showDotMenu: {
      type: Boolean,
      default: true,
    },
    showRadio: {
      type: Boolean,
      default: true,
    },
    schedule: {
      type: Object,
      default: () => {},
    },
    editingScheduleId: {
      type: Number,
      default: null,
    },
    feeScheduleTypes: {
      type: Array,
      default: () => [],
    },
    showInfoIcons: {
      type: Boolean,
      default: true,
    },
    isProgramBasedMode: Boolean,
    programOptions: Array,
  },
  data() {
    return {
      showDetail: this.schedule.isHistoricMaxFee && this.schedule.isActived,
      name: this.schedule.name,
      showEditDropDownMenu: false,
    };
  },
  provide() {
    return {
      translate: this.translate,
      formatNumber: this.formatNumber,
    };
  },
  computed: {
    currentTitleCmp() {
      if (this.schedule.isMaxFee) {
        return MaxFeeTitle;
      }
      if (this.schedule.isHistoricMaxFee) {
        return HistoricMaxFeeTitle;
      }
      if (Object.values(FEE_SCHEDULE_TYPES).includes(this.schedule.feeScheduleType)) {
        return AssetBasedFeeScheduleTitle;
      }
      return null;
    },
    currentDetailsViewCmp() {
      if (this.schedule.isMaxFee) {
        return MaxFeeDetailsView;
      }
      if (this.schedule.isHistoricMaxFee) {
        return HistoricMaxFeeDetailsView;
      }
      if (Object.values(FEE_SCHEDULE_TYPES).includes(this.schedule.feeScheduleType)) {
        return FeeScheduleDetailsView;
      }
      return null;
    },
    isSelectionEnabled() {
      if ((this.schedule.isHistoricMaxFee || this.schedule.isMaxFee) && this.schedule.isFirmData) {
        return this.showRadio;
      }
      return this.showRadio && !this.modifyingSchedule;
    },
    isEditable() {
      if ((this.schedule.isHistoricMaxFee || this.schedule.isMaxFee) && this.schedule.isFirmData) {
        return this.showDotMenu;
      }
      return this.showDotMenu && !this.modifyingCurrentSchedule && !this.schedule.isFirmSaved;
    },
    modifyingSchedule() {
      return this.editingScheduleId !== null;
    },
    modifyingCurrentSchedule() {
      return this.editingScheduleId === this.schedule.id;
    },
    isFeeScheduleEditable() {
      return (
        this.schedule.isMaxFee ||
        this.feeScheduleTypes.find(item => item.value === this.schedule.feeScheduleType) !==
          undefined
      );
    },
  },
  methods: {
    handleEdit() {
      if (!this.isFeeScheduleEditable) return;
      this.showEditDropDownMenu = false;
      this.$emit('item-edit-clicked', this.schedule.id);
    },
  },
};
</script>

<style lang="scss" module>
@import './styles/fee-schedule-item.scss';
</style>
